"use client";

import { GamePlayerLoadingGate } from "@/components/feature/game-player-loading-gate";
import { useFullscreenContext } from "@/contexts/fullscreen-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { useFullscreen } from "@/hooks/useFullscreen";
import {
  createGameAnalyticsRuntimeState,
  createUnityAnalyticsBridge,
  registerUnityAnalyticsBridge,
  unregisterUnityAnalyticsBridge,
} from "@/lib/analytics/game-events";
import {
  type UnityGameLoadFailureContext,
  type UnityGameLoadFailurePhase,
  type UnityGameLoadFailureSource,
  captureUnityGameLoadAborted,
  captureUnityGameLoadFailure,
  captureUnityGameLoaded,
  isTransientUnityLoadError,
} from "@/lib/analytics/unity-load-failure";
import {
  GAME_THEATER_PLAYER_BACKGROUND,
  GAME_THEATER_PLAYER_BG_CLASS,
} from "@/lib/constants/layout";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import {
  type UnityLoadAbortDecision,
  type UnityLoadAttempt,
  createUnityLoadAttempt,
} from "@/lib/unity/load-lifecycle";
import { startUnitySessionTeardown, waitForUnityQuitBarrier } from "@/lib/unity/load-teardown";
import { injectUnityLoaderScript } from "@/lib/unity/loader-injection";
import {
  createAbortError,
  isAbortError,
  parseRetryAfterMs,
  retryOnTransientError,
} from "@/lib/unity/retry";
import { attachWebGlContextGuard } from "@/lib/unity/webgl-context-guard";
import { usePostHog } from "posthog-js/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";

interface UnityGamePlayerProps {
  gameId: string;
  gameName: string;
  gameSlug: string;
  assetId: string;
  versionId: string;
  assetType?: string;
  className?: string;
  onFullscreenToggle?: (isFullscreen: boolean) => void;
  mobileAspectRatio?: string; // e.g., "3:4"
  platformEventTrackingEnabled?: boolean;
  /** Fills parent height (game page theater layout) instead of fixed aspect ratio. */
  fillContainer?: boolean;
}

interface UnityBuildFiles {
  loaderUrl: string;
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string; // wasm file
  buildName: string;
  /** Absolute CDN URL for Unity Application.streamingAssetsPath */
  streamingAssetsUrl: string;
}

interface UnityConfig {
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  streamingAssetsUrl: string;
  companyName: string;
  productName: string;
  productVersion: string;
  matchWebGLToCanvasSize?: boolean;
  showBanner: (msg: string, type: string) => void;
}

interface UnityInstance {
  Quit?: () => void | Promise<void>;
  SendMessage?: (objectName: string, methodName: string, value?: unknown) => void;
}

type PostHogCapture = ((event: string, properties?: Record<string, unknown>) => void) | undefined;

export { resetUnityQuitBarrierForTests } from "@/lib/unity/load-teardown";

/** Max rAF polls (~500 ms at 60 Hz) before giving up on canvas layout. */
const UNITY_CANVAS_LAYOUT_MAX_FRAMES = 30;

/**
 * WebKit browsers (Safari, Mobile Safari, iOS Chrome/Firefox) hit a keyboard
 * bootstrap race during createUnityInstance unless the canvas is focused first.
 */
export function isWebKitBrowser(userAgent: string = navigator.userAgent): boolean {
  // iOS Chrome/Firefox use WebKit but report "CriOS"/"FxiOS" instead of "Safari".
  if (/CriOS|FxiOS/i.test(userAgent)) {
    return true;
  }

  // Desktop Safari includes "Safari" but excludes Chromium-based browsers that also include it.
  return /Safari/i.test(userAgent) && !/Chrome|Chromium|Edg|OPR|Android/i.test(userAgent);
}

/** Best-effort layout size — CSS can report 0 on one property while another is already set. */
function getCanvasLayoutSize(canvas: HTMLCanvasElement): { width: number; height: number } {
  const rect = canvas.getBoundingClientRect();
  return {
    width: Math.max(rect.width, canvas.offsetWidth, canvas.clientWidth),
    height: Math.max(rect.height, canvas.offsetHeight, canvas.clientHeight),
  };
}

export function canvasHasLayout(canvas: HTMLCanvasElement): boolean {
  const { width, height } = getCanvasLayoutSize(canvas);
  return width > 0 && height > 0;
}

/**
 * Defers Unity init until after layout has settled. Always waits at least one
 * animation frame; polls until the canvas has non-zero dimensions or times out.
 *
 * Returns false when the component unmounts mid-wait; true when layout is ready
 * or when we time out but the caller is still mounted (Unity may still succeed).
 */
export async function waitForCanvasLayout(
  canvas: HTMLCanvasElement,
  isMounted?: () => boolean,
  maxFrames = UNITY_CANVAS_LAYOUT_MAX_FRAMES
): Promise<boolean> {
  for (let frame = 0; frame < maxFrames; frame++) {
    if (isMounted && !isMounted()) {
      return false;
    }

    // Yield to the browser so flex/grid sizing can run before we measure.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    if (canvasHasLayout(canvas)) {
      return true;
    }
  }

  return isMounted ? isMounted() : true;
}

/** Focus without scrolling the page; older browsers omit preventScroll support. */
function focusUnityCanvas(canvas: HTMLCanvasElement): void {
  try {
    canvas.focus({ preventScroll: true });
  } catch {
    canvas.focus();
  }
}

/**
 * Last gate before createUnityInstance: wait for layout, then pre-focus on WebKit
 * so Unity's input bootstrap does not race with keyboard handlers.
 */
async function prepareCanvasForUnityBootstrap(
  canvas: HTMLCanvasElement,
  isMounted?: () => boolean
): Promise<boolean> {
  const layoutReady = await waitForCanvasLayout(canvas, isMounted);
  if (!layoutReady) {
    return false;
  }

  if (isWebKitBrowser()) {
    focusUnityCanvas(canvas);
  }

  return true;
}

/** Host for Unity WebGL assets loaded into the playmath.org document. */
export const UNITY_CDN_SCRIPT_HOST = "assets.playmath.org";

/** Nest-safe install depth for overlapping `createUnityInstance` calls. */
let unityCdnScriptCrossOriginPatchDepth = 0;
let unityCdnScriptNativeCreateElement: typeof document.createElement | null = null;

function applyCrossOriginForUnityCdnScript(script: HTMLScriptElement): void {
  const srcDescriptor =
    Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, "src") ??
    Object.getOwnPropertyDescriptor(HTMLElement.prototype, "src");

  // Without a reliable src setter we cannot tell CDN vs third-party — skip rather
  // than forcing CORS mode on every script (e.g. Facebook Pixel has no ACAO).
  if (!srcDescriptor?.get || !srcDescriptor.set) {
    return;
  }

  Object.defineProperty(script, "src", {
    configurable: true,
    enumerable: true,
    get() {
      return srcDescriptor.get!.call(this);
    },
    set(value: string) {
      srcDescriptor.set!.call(this, value);
      try {
        if (new URL(script.src, document.baseURI).hostname === UNITY_CDN_SCRIPT_HOST) {
          script.crossOrigin = "anonymous";
        }
      } catch {
        // Ignore invalid script URLs; Unity still loads or fails on its own.
      }
    },
  });
}

function installUnityCdnScriptCrossOriginPatch(): boolean {
  if (unityCdnScriptCrossOriginPatchDepth === 0) {
    const nativeCreateElement = document.createElement;
    try {
      document.createElement = ((tagName: string, options?: ElementCreationOptions) => {
        const element =
          options === undefined
            ? Reflect.apply(nativeCreateElement, document, [tagName])
            : Reflect.apply(nativeCreateElement, document, [tagName, options]);
        if (String(tagName).toLowerCase() === "script") {
          applyCrossOriginForUnityCdnScript(element as HTMLScriptElement);
        }
        return element;
      }) as typeof document.createElement;
    } catch {
      // If createElement is non-writable, skip the patch and keep games loading.
      return false;
    }
    unityCdnScriptNativeCreateElement = nativeCreateElement;
  }

  unityCdnScriptCrossOriginPatchDepth += 1;
  return true;
}

function uninstallUnityCdnScriptCrossOriginPatch(): void {
  unityCdnScriptCrossOriginPatchDepth = Math.max(0, unityCdnScriptCrossOriginPatchDepth - 1);
  if (unityCdnScriptCrossOriginPatchDepth === 0 && unityCdnScriptNativeCreateElement) {
    document.createElement = unityCdnScriptNativeCreateElement;
    unityCdnScriptNativeCreateElement = null;
  }
}

/**
 * Unity's loader injects `.framework.js` without `crossOrigin`. On our CDN
 * (which already returns `Access-Control-Allow-Origin: *`), setting
 * `crossOrigin="anonymous"` restores real exception stacks instead of opaque
 * browser-stripped `Script error.` — see PostHog troubleshooting + PLA-700.
 *
 * Scoped around `createUnityInstance` so only Unity-created CDN scripts are
 * affected (not third-party tags like Facebook Pixel that lack ACAO).
 * Nested calls share one patch via a depth counter so overlapping boots cannot
 * restore `document.createElement` out from under each other.
 */
export async function withUnityCdnScriptCrossOrigin<T>(run: () => Promise<T>): Promise<T> {
  const installed = installUnityCdnScriptCrossOriginPatch();
  try {
    return await run();
  } finally {
    if (installed) {
      uninstallUnityCdnScriptCrossOriginPatch();
    }
  }
}

function getActiveUnityLoaderUrl(): string | undefined {
  return window.__playMathUnityActiveLoaderUrl;
}

function setActiveUnityLoaderUrl(url: string | undefined): void {
  window.__playMathUnityActiveLoaderUrl = url;
}

function removeInjectedUnityLoaderScripts(): void {
  for (const node of document.querySelectorAll('script[data-playmath-unity-loader="true"]')) {
    node.remove();
  }
}

function clearUnityLoaderGlobals(): void {
  setActiveUnityLoaderUrl(undefined);
  try {
    window.createUnityInstance = undefined;
  } catch {
    // Unity WebGL loader defines createUnityInstance as non-configurable on window.
    // The next loader script overwrites it when injected.
  }
}

/**
 * Adds mobile viewport meta tag if on mobile device (like working template)
 */
function addMobileViewportMeta(): { meta: HTMLMetaElement | null; acquired: boolean } {
  if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
    const existingMeta = document.querySelector(
      'meta[name="viewport"][data-playmath-unity="true"]'
    );
    if (existingMeta instanceof HTMLMetaElement) {
      const existingCount = Number(existingMeta.dataset.playmathUnityCount || "0");
      existingMeta.dataset.playmathUnityCount = String(existingCount + 1);
      return { meta: existingMeta, acquired: true };
    }

    const meta = document.createElement("meta");
    meta.name = "viewport";
    meta.dataset.playmathUnity = "true";
    meta.dataset.playmathUnityCount = "1";
    meta.content =
      "width=device-width, height=device-height, initial-scale=1.0, user-scalable=no, shrink-to-fit=yes";
    document.getElementsByTagName("head")[0].appendChild(meta);
    return { meta, acquired: true };
  }

  return { meta: null, acquired: false };
}

function getUnityPlayerShellLayout({
  theaterBackground,
  fillContainer,
  isPseudoFullscreen,
  isMobile,
  className,
  aspectStyle,
}: {
  theaterBackground: boolean;
  fillContainer: boolean;
  isPseudoFullscreen: boolean;
  isMobile: boolean;
  className: string;
  aspectStyle?: CSSProperties;
}) {
  const shellClassName = `relative w-full overflow-hidden ${
    theaterBackground ? GAME_THEATER_PLAYER_BG_CLASS : "bg-black"
  } ${
    fillContainer || isPseudoFullscreen
      ? "h-full rounded-none"
      : !isMobile
        ? "aspect-video rounded-3xl"
        : "rounded-none"
  } ${isPseudoFullscreen ? `fixed inset-0 ${Z_INDEX_CLASSES.FULLSCREEN_CONTAINER}` : ""} ${className}`;

  const shellStyle = isPseudoFullscreen
    ? { width: "100vw", height: "100dvh", aspectRatio: "unset" as const }
    : theaterBackground
      ? { ...aspectStyle, backgroundColor: GAME_THEATER_PLAYER_BACKGROUND }
      : aspectStyle;

  return { shellClassName, shellStyle };
}

/** Reports a single load failure; dedupes so one attempt emits at most one event. */
type ReportLoadFailure = (
  message: string,
  failureSource: UnityGameLoadFailureSource,
  failurePhase: UnityGameLoadFailurePhase
) => void;

function createPlayerLoadContext(
  gameId: string,
  gameSlug: string,
  gameName: string,
  assetId: string,
  versionId: string
): UnityGameLoadFailureContext {
  return {
    game_id: gameId,
    game_slug: gameSlug,
    game_name: gameName,
    asset_id: assetId,
    version_id: versionId,
  };
}

function releaseUnityViewportMeta(
  viewportMeta: HTMLMetaElement | null,
  ownsViewportRef: boolean
): void {
  if (viewportMeta?.dataset.playmathUnity !== "true" || !ownsViewportRef) {
    return;
  }
  const nextCount = Math.max(0, Number(viewportMeta.dataset.playmathUnityCount || "1") - 1);
  if (nextCount === 0) {
    viewportMeta.remove();
    return;
  }
  viewportMeta.dataset.playmathUnityCount = String(nextCount);
}

function captureAbortedUnityLoad(
  capture: PostHogCapture,
  context: UnityGameLoadFailureContext,
  loadAttempt: UnityLoadAttempt,
  decision: UnityLoadAbortDecision,
  wasBackgrounded: boolean
): void {
  if (decision.outcomeEmitted !== "aborted" || !decision.abortPhase) {
    return;
  }
  captureUnityGameLoadAborted(capture, context, {
    abortPhase: decision.abortPhase,
    elapsedMs: loadAttempt.elapsedMs(),
    wasBackgrounded,
  });
}

function finishUnityLoadTeardown(
  decision: UnityLoadAbortDecision,
  instance: UnityInstance | null,
  bootPromise: Promise<UnityInstance | null> | null,
  detachWebGlGuard: (() => void) | null,
  canvas: HTMLCanvasElement | null
): void {
  startUnitySessionTeardown({
    shouldAwaitBoot: decision.shouldAwaitBoot,
    instance,
    bootPromise,
    canvas,
    onSettled: () => detachWebGlGuard?.(),
  });
}

type UnityPlayerLoadSession = {
  mounted: boolean;
  wasBackgrounded: boolean;
  instance: UnityInstance | null;
  bootPromise: Promise<UnityInstance | null> | null;
  canvas: HTMLCanvasElement | null;
  viewportMeta: HTMLMetaElement | null;
  ownsViewportRef: boolean;
  detachWebGlGuard: (() => void) | null;
};

function createLoadFailureReporters(
  capture: PostHogCapture,
  loadContext: UnityGameLoadFailureContext,
  loadAttempt: UnityLoadAttempt,
  session: UnityPlayerLoadSession
): { emitFailure: ReportLoadFailure; reportFailure: ReportLoadFailure } {
  const emitFailure: ReportLoadFailure = (message, failureSource, failurePhase) => {
    captureUnityGameLoadFailure(capture, loadContext, message, failureSource, {
      failurePhase,
      elapsedMs: loadAttempt.elapsedMs(),
      wasBackgrounded: session.wasBackgrounded,
    });
  };
  const reportFailure: ReportLoadFailure = (message, failureSource, failurePhase) => {
    if (!loadAttempt.fail()) return;
    emitFailure(message, failureSource, failurePhase);
  };
  return { emitFailure, reportFailure };
}

function completeUnityLoadIfReady(
  session: UnityPlayerLoadSession,
  loadAttempt: UnityLoadAttempt,
  capture: PostHogCapture,
  loadContext: UnityGameLoadFailureContext,
  setLoading: (loading: boolean) => void
): void {
  if (loadAttempt.isAborted() || !session.mounted || !session.instance) {
    return;
  }
  if (loadAttempt.markLoaded()) {
    captureUnityGameLoaded(capture, loadContext, {
      elapsedMs: loadAttempt.elapsedMs(),
      wasBackgrounded: session.wasBackgrounded,
    });
  }
  setLoading(false);
}

async function runUnityPlayerLoadAttempt(
  session: UnityPlayerLoadSession,
  loadAttempt: UnityLoadAttempt,
  loadContext: UnityGameLoadFailureContext,
  capture: PostHogCapture,
  canvas: HTMLCanvasElement | null,
  assetId: string,
  versionId: string,
  setProgress: (progress: number) => void,
  setError: (error: string) => void,
  setLoading: (loading: boolean) => void
): Promise<void> {
  loadAttempt.start();
  const { emitFailure, reportFailure } = createLoadFailureReporters(
    capture,
    loadContext,
    loadAttempt,
    session
  );

  try {
    const viewportRegistration = addMobileViewportMeta();
    session.viewportMeta = viewportRegistration.meta;
    session.ownsViewportRef = viewportRegistration.acquired;
    if (canvas) {
      session.detachWebGlGuard = attachWebGlContextGuard(canvas);
    }

    await waitForUnityQuitBarrier();
    if (loadAttempt.isAborted() || !session.mounted) {
      return;
    }

    session.bootPromise = loadUnityGame(
      assetId,
      versionId,
      canvas,
      () => session.mounted && !loadAttempt.isAborted(),
      setProgress,
      setError,
      setLoading,
      (msg) => reportFailure(msg, "unity_runtime_error", "unity_runtime"),
      {
        signal: loadAttempt.signal,
        onEnterBooting: () => loadAttempt.enterBooting(),
      }
    );
    session.instance = await session.bootPromise;
    completeUnityLoadIfReady(session, loadAttempt, capture, loadContext, setLoading);
  } catch (err) {
    if (isAbortError(err) || loadAttempt.isAborted()) {
      return;
    }
    handleUnityLoadError(err, {
      mounted: session.mounted,
      shouldReportFailure: () => loadAttempt.fail(),
      reportFailure: emitFailure,
      setError,
      setLoading,
    });
  }
}

function handleUnityLoadError(
  err: unknown,
  options: {
    mounted: boolean;
    shouldReportFailure: () => boolean;
    reportFailure: ReportLoadFailure;
    setError: (message: string) => void;
    setLoading: (loading: boolean) => void;
  }
) {
  const isPermissionDenied =
    err instanceof Error && (err as GameLoadError).isPermissionDenied === true;
  const message = getGameLoadErrorMessage(err);
  const failurePhase = getGameLoadFailurePhase(err);
  const shouldReport = options.shouldReportFailure();

  if (!isPermissionDenied) {
    console.error("Failed to load Unity game:", err);
    if (shouldReport) {
      options.reportFailure(message, "load_exception", failurePhase);
    }
  }

  if (!options.mounted) return;

  options.setError(message);
  options.setLoading(false);
}

export function UnityGamePlayer({
  gameId,
  gameName,
  gameSlug,
  assetId,
  versionId,
  assetType = "unity",
  className = "",
  onFullscreenToggle,
  mobileAspectRatio,
  platformEventTrackingEnabled = false,
  fillContainer = false,
}: UnityGamePlayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const posthog = usePostHog();
  const posthogCaptureRef = useRef<PostHogCapture>(undefined);
  const instanceId = useId();
  const unityBridgeId = `unity_${gameId}_${assetId}_${versionId}_${instanceId}`;
  const analyticsRuntimeState = useMemo(
    () => createGameAnalyticsRuntimeState(),
    [assetId, gameId, versionId]
  );
  const { registerToggleFullscreen, registerFullscreenState } = useFullscreenContext();

  // Use the fullscreen hook
  const { isFullscreen, isPseudoFullscreen, toggleFullscreen } = useFullscreen({
    containerRef,
    isMobile,
    onFullscreenToggle,
  });

  // Register toggle function and state with context
  useEffect(() => {
    registerToggleFullscreen(toggleFullscreen);
  }, [toggleFullscreen, registerToggleFullscreen]);

  useEffect(() => {
    registerFullscreenState(isFullscreen, isPseudoFullscreen);
  }, [isFullscreen, isPseudoFullscreen, registerFullscreenState]);

  useEffect(() => {
    posthogCaptureRef.current = posthog?.capture?.bind(posthog);
    posthog?.register_for_session?.({
      last_unity_game_slug: gameSlug,
      last_unity_game_name: gameName,
    });
  }, [gameName, gameSlug, posthog]);

  useEffect(() => {
    registerUnityAnalyticsBridge(
      unityBridgeId,
      createUnityAnalyticsBridge(
        {
          enabled: platformEventTrackingEnabled,
          game_id: gameId,
          game_slug: gameSlug,
          game_name: gameName,
          source: "unity_webgl",
          asset_id: assetId,
          asset_type: assetType,
          version_id: versionId,
          page_path: window.location.pathname,
        },
        posthog?.capture?.bind(posthog),
        analyticsRuntimeState
      )
    );

    return () => {
      unregisterUnityAnalyticsBridge(unityBridgeId);
    };
  }, [
    assetId,
    assetType,
    analyticsRuntimeState,
    gameId,
    gameName,
    gameSlug,
    platformEventTrackingEnabled,
    posthog,
    unityBridgeId,
    versionId,
  ]);

  useEffect(() => {
    const session: UnityPlayerLoadSession = {
      mounted: true,
      wasBackgrounded: typeof document !== "undefined" && document.visibilityState === "hidden",
      instance: null,
      bootPromise: null,
      canvas: canvasRef.current,
      viewportMeta: null,
      ownsViewportRef: false,
      detachWebGlGuard: null,
    };
    const loadAttempt = createUnityLoadAttempt();
    const loadContext = createPlayerLoadContext(gameId, gameSlug, gameName, assetId, versionId);

    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        session.wasBackgrounded = true;
      }
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    void runUnityPlayerLoadAttempt(
      session,
      loadAttempt,
      loadContext,
      posthogCaptureRef.current,
      canvasRef.current,
      assetId,
      versionId,
      setProgress,
      setError,
      setLoading
    );

    return () => {
      session.mounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      releaseUnityViewportMeta(session.viewportMeta, session.ownsViewportRef);
      // Attach a handler before abort() so in-flight fetch/inject rejects
      // (fired synchronously from AbortSignal) are not unhandled during unmount.
      void session.bootPromise?.catch(() => undefined);
      const decision = loadAttempt.abort();
      captureAbortedUnityLoad(
        posthogCaptureRef.current,
        loadContext,
        loadAttempt,
        decision,
        session.wasBackgrounded
      );
      // Park the canvas and keep the context-loss guard until Quit() finishes.
      // React unmount would otherwise yank the WebGL handle mid-teardown.
      finishUnityLoadTeardown(
        decision,
        session.instance,
        session.bootPromise,
        session.detachWebGlGuard,
        session.canvas
      );
    };
  }, [assetId, versionId, gameId, gameName, gameSlug]);

  const useAspectRatio = !fillContainer && !isPseudoFullscreen;
  const cssAspectRatio = (mobileAspectRatio || "3:4").replace(":", "/");
  const aspectStyle = useAspectRatio && isMobile ? { aspectRatio: cssAspectRatio } : undefined;

  const theaterBackground = fillContainer && !isPseudoFullscreen;
  const { shellClassName, shellStyle } = getUnityPlayerShellLayout({
    theaterBackground,
    fillContainer,
    isPseudoFullscreen,
    isMobile,
    className,
    aspectStyle,
  });

  return (
    <div ref={containerRef} className={shellClassName} style={shellStyle}>
      <GamePlayerLoadingGate
        loading={loading && !error}
        progress={progress}
        isMobile={isMobile}
        className="absolute inset-0"
      >
        <canvas
          ref={canvasRef}
          id={`unity-canvas-${assetId}-${versionId}`}
          width={600}
          height={800}
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            background: "#FFFFFF",
          }}
          tabIndex={-1}
        />
      </GamePlayerLoadingGate>

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white p-8">
          <div className="text-center">
            <div className="text-xl font-semibold mb-2 text-red-400">Failed to load game</div>
            <div className="text-sm text-gray-400">{error}</div>
          </div>
        </div>
      )}
    </div>
  );
}

interface GameLoadError extends Error {
  isPermissionDenied?: boolean;
  failurePhase?: UnityGameLoadFailurePhase;
  status?: number;
  retryAfterMs?: number;
}

function createGameLoadError(
  message: string,
  options?: {
    isPermissionDenied?: boolean;
    failurePhase?: UnityGameLoadFailurePhase;
    status?: number;
    retryAfterMs?: number;
  }
): GameLoadError {
  const error = new Error(message) as GameLoadError;
  error.isPermissionDenied = options?.isPermissionDenied ?? false;
  error.failurePhase = options?.failurePhase;
  if (options?.status !== undefined) {
    error.status = options.status;
  }
  if (options?.retryAfterMs !== undefined) {
    error.retryAfterMs = options.retryAfterMs;
  }
  return error;
}

function getGameLoadErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message || "Failed to load game";
  }

  if (typeof err === "string") {
    return err.trim() || "Failed to load game";
  }

  if (err && typeof err === "object" && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  return "Failed to load game";
}

function getGameLoadFailurePhase(err: unknown): UnityGameLoadFailurePhase {
  if (err instanceof Error) {
    return (err as GameLoadError).failurePhase ?? "unknown";
  }

  return "unknown";
}

function throwWithGameLoadFailurePhase(
  err: unknown,
  failurePhase: UnityGameLoadFailurePhase
): never {
  if (err instanceof Error) {
    const gameLoadError = err as GameLoadError;
    gameLoadError.failurePhase ??= failurePhase;
    throw gameLoadError;
  }

  throw createGameLoadError(getGameLoadErrorMessage(err), { failurePhase });
}

function getFileListErrorMessage(status: number, apiMessage?: string): string {
  if (status === 401) {
    return "Sign in to preview this game.";
  }
  if (status === 403) {
    return "You don't have permission to preview this game.";
  }
  return apiMessage || "Failed to fetch file list";
}

/**
 * Fetches file list and detects Unity build files
 */
async function fetchAndDetectFiles(
  assetId: string,
  versionId: string,
  signal?: AbortSignal
): Promise<UnityBuildFiles> {
  let response: Response;

  try {
    response = await fetch(`/api/game-assets/${assetId}/versions/${versionId}/list-files`, {
      signal,
    });
  } catch (err) {
    throwWithGameLoadFailurePhase(err, "file_list_fetch");
  }

  if (!response.ok) {
    let apiMessage: string | undefined;
    try {
      const errorBody = await response.json();
      if (typeof errorBody?.error === "string") {
        apiMessage = errorBody.error;
      }
    } catch {
      // Keep default message when response body is not JSON.
    }

    throw createGameLoadError(getFileListErrorMessage(response.status, apiMessage), {
      isPermissionDenied: response.status === 403,
      failurePhase: "file_list_fetch",
      status: response.status,
      retryAfterMs: parseRetryAfterMs(response.headers?.get("Retry-After")),
    });
  }

  let result: unknown;
  try {
    result = await response.json();
  } catch (err) {
    throwWithGameLoadFailurePhase(err, "file_list_fetch");
  }

  // Handle error response format
  if (result && typeof result === "object" && "error" in result) {
    const apiError = (result as { error?: unknown }).error;
    if (apiError) {
      throw createGameLoadError(String(apiError), { failurePhase: "file_list_fetch" });
    }
  }

  const files: string[] = Array.isArray(result) ? result : [];

  try {
    return detectUnityBuildFiles(files, assetId, versionId);
  } catch (err) {
    throwWithGameLoadFailurePhase(err, "build_file_detection");
  }
}

/** Prefer brotli, then gzip, then uncompressed Unity artifacts. */
function unityArtifactCompressionRank(file: string, extension: string): number | null {
  if (file.endsWith(`${extension}.br`)) return 0;
  if (file.endsWith(`${extension}.gz`)) return 1;
  if (file.endsWith(extension)) return 2;
  return null;
}

/** Extension-only lookup for Unity "Name Files As Hashes" builds. */
function findUnityBuildArtifactByExtension(files: string[], extension: string): string | null {
  const matches = files
    .map((file) => {
      const rank = unityArtifactCompressionRank(file, extension);
      return rank === null ? null : { file, rank };
    })
    .filter((match): match is { file: string; rank: number } => match !== null)
    .sort((a, b) => a.rank - b.rank || a.file.localeCompare(b.file));

  return matches[0]?.file ?? null;
}

/**
 * Finds a Unity build artifact by shared basename first, then by extension.
 * Unity's "Name Files As Hashes" option gives each file a unique MD5 basename,
 * so data/framework/wasm no longer share the loader's name.
 */
function findUnityBuildArtifact(
  files: string[],
  fileSet: Set<string>,
  baseName: string,
  extension: string
): string | null {
  const sameBaseMatch = [
    `${baseName}${extension}.br`,
    `${baseName}${extension}.gz`,
    `${baseName}${extension}`,
  ].find((candidate) => fileSet.has(candidate));

  return sameBaseMatch ?? findUnityBuildArtifactByExtension(files, extension);
}

/**
 * Detects Unity build files from a list of file names
 * Handles various compression formats (.br, .gz) and naming patterns
 */
export function detectUnityBuildFiles(
  files: string[],
  assetId: string,
  versionId: string
): UnityBuildFiles {
  const baseUrl = `https://assets.playmath.org/game-assets/${assetId}/${versionId}`;

  // Create a Set for O(1) file lookups instead of O(n) find operations
  const fileSet = new Set(files);

  // Find the loader file (always uncompressed)
  const loaderFile = files.find((f) => f.endsWith(".loader.js"));
  if (!loaderFile) {
    throw new Error("No Unity loader file (.loader.js) found");
  }

  // Extract build name from loader file
  const buildName = loaderFile.replace(".loader.js", "");

  // Find required files (basename match, then hash-named extension fallback)
  const dataFile = findUnityBuildArtifact(files, fileSet, buildName, ".data");
  const frameworkFile = findUnityBuildArtifact(files, fileSet, buildName, ".framework.js");
  const wasmFile = findUnityBuildArtifact(files, fileSet, buildName, ".wasm");

  if (!dataFile) {
    throw new Error(`Unity data file not found (expected ${buildName}.data or *.data[.br|.gz])`);
  }
  if (!frameworkFile) {
    throw new Error(
      `Unity framework file not found (expected ${buildName}.framework.js or *.framework.js[.br|.gz])`
    );
  }
  if (!wasmFile) {
    throw new Error(
      `Unity WebAssembly file not found (expected ${buildName}.wasm or *.wasm[.br|.gz])`
    );
  }

  return {
    loaderUrl: buildUnityAssetUrl(baseUrl, loaderFile),
    dataUrl: buildUnityAssetUrl(baseUrl, dataFile),
    frameworkUrl: buildUnityAssetUrl(baseUrl, frameworkFile),
    codeUrl: buildUnityAssetUrl(baseUrl, wasmFile),
    buildName,
    streamingAssetsUrl: `${baseUrl}/StreamingAssets`,
  };
}

function buildUnityAssetUrl(baseUrl: string, filePath: string): string {
  const encodedPath = filePath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  return `${baseUrl}/${encodedPath}`;
}

/**
 * Creates a Unity instance from an already-injected loader script.
 * Waits for canvas layout/focus before calling into Unity.
 */
async function createUnityInstanceFromLoader(
  canvas: HTMLCanvasElement,
  config: UnityConfig,
  setProgress: (progress: number) => void,
  setLoading: (loading: boolean) => void,
  isMounted?: () => boolean,
  signal?: AbortSignal
): Promise<UnityInstance | null> {
  if (signal?.aborted) {
    return null;
  }

  if (!window.createUnityInstance) {
    throw createGameLoadError("Unity loader script loaded but createUnityInstance not available", {
      failurePhase: "loader_script_injection",
    });
  }

  const ready = await prepareCanvasForUnityBootstrap(canvas, isMounted);
  if (!ready || signal?.aborted) {
    // Component unmounted or layout wait aborted — skip instance creation.
    return null;
  }

  try {
    const instance = await withUnityCdnScriptCrossOrigin(() =>
      window.createUnityInstance!(canvas, config, (progress) => {
        setProgress(Math.round(progress * 100));
      })
    );
    if (signal?.aborted) {
      return instance;
    }
    setLoading(false);
    return instance;
  } catch (err) {
    if (signal?.aborted || isAbortError(err)) {
      throw createAbortError();
    }
    throwWithGameLoadFailurePhase(err, "unity_instance_creation");
  }
}

/**
 * Ensures the correct Unity loader is on the page, then creates an instance.
 *
 * Loader lifecycle (only one build's loader can live on window at a time):
 * 1. Same loader URL already active → reuse createUnityInstance, no new script tag.
 * 2. createUnityInstance exists but we lost track of the URL (e.g. page refresh path) → adopt it.
 * 3. Different build → tear down old script/globals, inject fresh loader, then create instance.
 */
async function loadUnityLoaderScript(
  loaderUrl: string,
  canvas: HTMLCanvasElement,
  config: UnityConfig,
  setProgress: (progress: number) => void,
  setLoading: (loading: boolean) => void,
  isMounted?: () => boolean,
  signal?: AbortSignal
): Promise<UnityInstance | null> {
  // Wait for any prior Unity instance to finish Quit() before loading again.
  await waitForUnityQuitBarrier();
  if (signal?.aborted) {
    return null;
  }

  const activeLoaderUrl = getActiveUnityLoaderUrl();
  const canReuseLoader =
    typeof window.createUnityInstance === "function" && activeLoaderUrl === loaderUrl;

  if (canReuseLoader) {
    return createUnityInstanceFromLoader(
      canvas,
      config,
      setProgress,
      setLoading,
      isMounted,
      signal
    );
  }

  // Loader function present but URL not tracked — assume it matches and record it.
  if (typeof window.createUnityInstance === "function" && !activeLoaderUrl) {
    setActiveUnityLoaderUrl(loaderUrl);
    return createUnityInstanceFromLoader(
      canvas,
      config,
      setProgress,
      setLoading,
      isMounted,
      signal
    );
  }

  // Switching builds: remove stale script tags and reset window.createUnityInstance.
  if (activeLoaderUrl && activeLoaderUrl !== loaderUrl) {
    removeInjectedUnityLoaderScripts();
    clearUnityLoaderGlobals();
  }

  try {
    await retryOnTransientError(() => injectUnityLoaderScript(loaderUrl, signal), {
      signal,
      isRetryable: isTransientUnityLoadError,
    });
  } catch (err) {
    if (isAbortError(err) || signal?.aborted) {
      throw createAbortError();
    }
    throwWithGameLoadFailurePhase(err, "loader_script_injection");
  }
  if (signal?.aborted) {
    return null;
  }
  setActiveUnityLoaderUrl(loaderUrl);

  return createUnityInstanceFromLoader(canvas, config, setProgress, setLoading, isMounted, signal);
}

/**
 * Loads and creates the Unity game instance
 */
async function loadUnityGame(
  assetId: string,
  versionId: string,
  canvas: HTMLCanvasElement | null,
  isMounted: () => boolean,
  setProgress: (progress: number) => void,
  setError: (error: string) => void,
  setLoading: (loading: boolean) => void,
  onUnityBannerError?: (message: string) => void,
  options?: {
    signal?: AbortSignal;
    onEnterBooting?: () => void;
  }
): Promise<UnityInstance | null> {
  if (!canvas) {
    throw createGameLoadError("Canvas element is not available", { failurePhase: "canvas_setup" });
  }

  const signal = options?.signal;
  const buildFiles = await retryOnTransientError(
    () => fetchAndDetectFiles(assetId, versionId, signal),
    {
      signal,
      isRetryable: isTransientUnityLoadError,
    }
  );

  // User may have navigated away while the file list was fetching.
  if (!isMounted() || signal?.aborted) {
    return null;
  }

  options?.onEnterBooting?.();

  const config: UnityConfig = {
    dataUrl: buildFiles.dataUrl,
    frameworkUrl: buildFiles.frameworkUrl,
    codeUrl: buildFiles.codeUrl,
    streamingAssetsUrl: buildFiles.streamingAssetsUrl,
    companyName: "PlayMath",
    productName: buildFiles.buildName || "Unity Game",
    productVersion: "1.0",
    // Use Unity's default canvas sizing behavior (matchWebGLToCanvasSize is not set)
    // This matches the working template configuration
    showBanner: (msg: string, type: string) => {
      if (type === "error") {
        // Filter out common Unity warnings that aren't critical
        if (msg.includes("querySelector") || msg.includes("not a valid selector")) {
          console.warn("Unity selector warning (non-critical):", msg);
          return;
        }
        setError(msg);
        onUnityBannerError?.(msg);
      }
    },
  };

  if (isMounted() && canvas && !signal?.aborted) {
    return await loadUnityLoaderScript(
      buildFiles.loaderUrl,
      canvas,
      config,
      setProgress,
      setLoading,
      isMounted,
      signal
    );
  }
  return null;
}

// Extend window type for Unity
declare global {
  interface Window {
    __playMathUnityActiveLoaderUrl?: string;
    createUnityInstance?: (
      canvas: HTMLCanvasElement,
      config: UnityConfig,
      onProgress?: (progress: number) => void
    ) => Promise<UnityInstance>;
  }
}
